/**
 * One pending Freigabe-Anfrage with its Grant / Deny controls (W2).
 *
 * Extracted from FreigabenView so the Bearbeitungsmaske can decide a request on the
 * Eingang it is showing, without a second copy of the duration and decline rules.
 * Renders a fragment: the caller supplies the row wrapper (li in a list, div on its own).
 */
import { useState } from 'react';
import { Check, X, Mail } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useDecideRequest } from '../hooks/useFreigabe';
import type { AccessRequestItem, DecideAccessRequestBody } from '../../../lib/api-client';

/** Short id for display (never the full uuid). */
function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

/** Minimum length of a decline justification (Bereich 1, 3.3 — matches the api guard). */
const DECLINE_MIN = 20;

/** target_role → the German label of the party the request is addressed to. */
const TARGET_ROLE_LABELS: Record<string, string> = {
  sender: 'Absender',
  recipient: 'Empfänger',
};

type Duration = NonNullable<DecideAccessRequestBody['duration']>;

/** Grant-duration presets (Bereich 1, 3.3) — value → German label. */
const DURATION_OPTIONS: ReadonlyArray<{ value: Duration; label: string }> = [
  { value: 'permanent', label: 'Dauerhaft' },
  { value: '1_day', label: '1 Tag' },
  { value: '7_days', label: '7 Tage' },
  { value: '30_days', label: '30 Tage' },
  { value: 'until_date', label: 'bis Datum' },
];

/** Today as YYYY-MM-DD — the min selectable "bis Datum" (no past expiry). */
function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function FreigabeEntscheid({
  request: r,
  onFeedback,
  onError,
  heading = 'subject',
}: {
  request: AccessRequestItem;
  onFeedback?: (msg: string) => void;
  onError?: (msg: string) => void;
  /** Row lead: 'subject' (decider inbox, default) or 'requester' (Freigabe-Cockpit). */
  heading?: 'subject' | 'requester';
}) {
  const decide = useDecideRequest((m) => onError?.(m));
  const [duration, setDuration] = useState<Duration>('permanent');
  const [untilDate, setUntilDate] = useState('');
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState('');

  function onGrant() {
    // Only the "bis Datum" preset carries a date; the api derives expires_at from the rest.
    decide.mutate(
      {
        requestId: r.id,
        decision: 'grant',
        duration,
        ...(duration === 'until_date' ? { until_date: untilDate } : {}),
      },
      { onSuccess: () => onFeedback?.('Freigabe erteilt') },
    );
  }

  function onConfirmDeny() {
    decide.mutate(
      { requestId: r.id, decision: 'deny', review_note: reason.trim() },
      {
        onSuccess: () => {
          onFeedback?.('Freigabe abgelehnt');
          setDenying(false);
          setReason('');
        },
      },
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Mail className="size-3.5" />
        </span>
        {/* Issue 33: a decider is a full-tier participant, so subject/requester/Begründung
            are not a tier leak here; the api nulls all three on requester-facing surfaces. */}
        <div className="min-w-0 flex-1">
          {heading === 'requester' ? (
            <p className="truncate font-medium text-foreground">
              {r.requester_name?.trim() ? r.requester_name : 'Ein Benutzer'}
            </p>
          ) : (
            <p className="truncate font-medium text-foreground">
              {r.subject?.trim() ? r.subject : `E-Mail ${shortId(r.comm_id)}`}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {heading === 'requester'
              ? 'bittet um Vollzugriff'
              : `${r.requester_name?.trim() ? r.requester_name : 'Ein Benutzer'} bittet um Vollzugriff`}{' '}
            · angefragt am {new Date(r.created_at).toLocaleDateString('de-CH')}
            {r.target_role && TARGET_ROLE_LABELS[r.target_role]
              ? ` · Anfrage an den ${TARGET_ROLE_LABELS[r.target_role]}`
              : ''}
          </p>
          {r.reason?.trim() && (
            <p className="mt-1 text-xs text-foreground">
              <span className="text-muted-foreground">Begründung:</span> {r.reason}
            </p>
          )}
          {(r.sync_level_at_request === 2 || r.sync_level_at_request === 3) && (
            <p className="mt-1 text-xs text-muted-foreground">
              Der Inhalt dieser E-Mail wurde nie gespeichert (Stufe {r.sync_level_at_request}) und
              wird erst bei der Freigabe nachgeladen.
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Bereich 1 (3.3): grant duration → comm_access_grant.expires_at. */}
          <select
            aria-label="Dauer der Freigabe"
            className="rounded-md border border-border bg-card px-2 py-1.5 text-xs"
            value={duration}
            onChange={(e) => setDuration(e.target.value as Duration)}
            disabled={decide.isPending}
          >
            {DURATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {duration === 'until_date' && (
            <input
              type="date"
              aria-label="Freigabe bis Datum"
              className="rounded-md border border-border bg-card px-2 py-1.5 text-xs"
              min={todayISODate()}
              value={untilDate}
              onChange={(e) => setUntilDate(e.target.value)}
              disabled={decide.isPending}
            />
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDenying((d) => !d);
              setReason('');
            }}
            disabled={decide.isPending}
          >
            <X className="size-4" />
            Ablehnen
          </Button>
          <Button
            size="sm"
            onClick={onGrant}
            // "bis Datum" is only grantable once a date is picked (api → 400 otherwise).
            disabled={decide.isPending || (duration === 'until_date' && !untilDate)}
          >
            <Check className="size-4" />
            Zugriff freigeben
          </Button>
        </div>
      </div>
      {/* Bereich 1 (3.3): a decline needs a ≥20-char justification (audit-relevant). */}
      {denying && (
        <div className="ml-10 flex flex-col gap-2">
          <textarea
            className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-xs"
            rows={2}
            placeholder="Begründung der Ablehnung (mind. 20 Zeichen) …"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex items-center justify-end gap-2">
            <span className="mr-auto text-xs text-muted-foreground">
              {reason.trim().length}/{DECLINE_MIN}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setDenying(false);
                setReason('');
              }}
            >
              Abbrechen
            </Button>
            <Button
              size="sm"
              onClick={onConfirmDeny}
              disabled={decide.isPending || reason.trim().length < DECLINE_MIN}
            >
              Ablehnung bestätigen
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
